// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * ViewportManager — orchestrates one or more synchronised MapRenderer
 * instances inside a CSS grid container.
 *
 * **Scope** — creates/destroys MapRenderer instances to match a target
 * layout (1, 2h, 2v, or 4 panels), wires each one's `move` event to
 * mirror camera state to every sibling, and tracks a "primary" index
 * that drives playback, screenshots, and the info panel.
 *
 * **Dataset-agnostic.** ViewportManager does not load or own datasets.
 * It exposes renderers via `getPrimary()` / `getRendererAt()`; callers
 * (main.ts) keep a parallel per-panel dataset state array and load
 * into the right renderer. ViewportManager fires `onPrimaryChange` and
 * `onLayoutChange` callbacks so callers can react.
 *
 * **Primary affordance.** Each non-primary panel gets a small pill
 * button ("1" / "2" / "3" / "4") in the top-left that, when clicked,
 * promotes that panel to primary. The primary panel's button is
 * styled differently and does nothing on click. The primary panel
 * also gets a subtle accent border.
 *
 * **Camera sync** uses `jumpTo` (not `easeTo`) on siblings so motion is
 * instantaneous and doesn't compound across panels. A `syncLock` flag
 * prevents the move event we dispatch on siblings from re-entering
 * the sync path — without it, every `jumpTo` would fire another
 * `move` and we'd recurse forever.
 *
 * The module-level `activeRenderer` singleton in mapRenderer.ts is
 * kept in sync via `setActiveMapRenderer()` so screenshotService
 * (which can't easily take a renderer argument) always captures the
 * primary.
 */

import { MapRenderer, setActiveMapRenderer } from './mapRenderer'
import type { ColorScaleDisplay } from './colorScaleDisplay'
import { logger } from '../utils/logger'
import { emit } from '../analytics'
import { t } from '../i18n'
import { maxVideoPanels } from '../utils/deviceCapability'

/** Map the internal ViewLayout string into the bucket the analytics
 * schema understands ('1globe' / '2globes' / '4globes'). */
function layoutForEvent(layout: ViewLayout): '1globe' | '2globes' | '4globes' {
  if (layout === '1') return '1globe'
  if (layout === '4') return '4globes'
  return '2globes'
}

/** Viewport layout identifier. */
export type ViewLayout = '1' | '2h' | '2v' | '4'

/** How many panels a given layout renders. */
const PANEL_COUNT: Record<ViewLayout, number> = {
  '1': 1,
  '2h': 2,
  '2v': 2,
  '4': 4,
}

/**
 * Reduce a requested layout to one the device can actually hold.
 *
 * A phone cannot keep more than {@link MAX_VIDEO_PANELS_PHONE} video
 * decoders alive; the third one crashes the tab while it is still
 * loading (terraviz#230). Nothing downstream can recover from that, and
 * a deep link asks for four panels without knowing what device it is on, so
 * the request is reduced here rather than honoured and then survived.
 *
 * Orientation picks the two-panel variant, because it decides whether
 * the result is usable: stacked on a portrait phone gives each globe
 * the full width, where side-by-side would give it under 200px.
 *
 * Pure and exported for tests. `maxPanels` is the caller's cap so the
 * policy stays in `deviceCapability`.
 */
export function clampLayoutToPanelBudget(
  layout: ViewLayout,
  maxPanels: number,
  portrait: boolean,
): ViewLayout {
  if (PANEL_COUNT[layout] <= maxPanels) return layout
  if (maxPanels <= 1) return '1'
  return portrait ? '2v' : '2h'
}

/**
 * CSS `grid-template` shorthand for each layout. The number of columns
 * in the area strings MUST match the number of column sizes after `/`
 * or CSS grid falls back to implicit tracks and the visible area
 * shrinks (bug seen in Phase 2: `2v` was written as `"a" "b" / 1fr 1fr`
 * which only populated the left half of the container).
 */
const GRID_TEMPLATE: Record<ViewLayout, string> = {
  '1': '"a" / 1fr',
  '2h': '"a b" / 1fr 1fr',
  '2v': '"a" "b" / 1fr',
  '4': '"a b" "c d" / 1fr 1fr',
}

/** Callbacks fired by the manager so callers can keep parallel state. */
export interface ViewportManagerCallbacks {
  /**
   * AYNI: most globes the account's plan allows side by side (Free and
   * Plus: 1; planAccess `maxGlobes`). Combined with the device's own budget, so
   * a deep link or the Tools picker cannot open more than either.
   */
  panelLimit?(): number
  /**
   * Fired after a `setLayout` call that changed the panel count, with
   * the new count. Callers should resize their parallel per-panel
   * state arrays. Not fired on init — callers know the starting count.
   */
  onLayoutChange?(newCount: number, oldCount: number): void
  /**
   * Fired when `promoteToPrimary` changes the primary index, with
   * the new and previous indices. Callers should rewire UI bound to
   * "the current dataset" (info panel, playback controls, URL, etc.).
   */
  onPrimaryChange?(newIndex: number, oldIndex: number): void
  /**
   * Telemetry-only — return the dataset id currently loaded in
   * the given slot (or null when the panel shows the default
   * Earth). Forwarded into each MapRenderer at init so
   * `camera_settled` events can carry `layer_id`. Optional;
   * absent → MapRenderer reports null layer_id.
   */
  getLayerIdForSlot?(slot: number): string | null
  /**
   * AYNI (multi-globe plan, phase 0): the dataset chip on a panel was
   * pressed — its title, or "+ Add a dataset" on an empty panel. The
   * caller opens Browse to load into that panel.
   */
  onDatasetChip?(slot: number, anchor: HTMLElement): void
  /**
   * AYNI (multi-globe plan, phase 1): the time chip on a panel was
   * pressed — the caller opens the menu of time rules for that globe.
   */
  onTimeChip?(slot: number, anchor: HTMLElement): void
}

/** One panel in the grid. */
interface Viewport {
  index: number
  container: HTMLDivElement
  renderer: MapRenderer
  /** AYNI: the row at the top-left: the number pill, the dataset chip, the time chip and the date. */
  head: HTMLDivElement
  indicator: HTMLButtonElement
  datasetChip: HTMLButtonElement
  timeChip: HTMLButtonElement
  dateLabel: HTMLSpanElement
  /** AYNI (multi-globe plan, phase 3): who drives this globe in a meeting; made when someone does. */
  driverChip: HTMLSpanElement | null
  /** Floating per-panel legend element — lazily created the first
   *  time the panel needs one, and toggled via classList thereafter. */
  legend: HTMLButtonElement | null
  /** Floating "this panel is not on the labelled date" notice —
   *  lazily created the first time a panel needs one. */
  timeNotice: HTMLDivElement | null
  /** Date this panel is actually showing, when it contradicts the label. */
  noticeDate: string | null
  /** True once this panel's stream has failed terminally. */
  streamFailed: boolean
  /** Floating per-panel colorbar for data-encoded datasets. Replaced
   *  wholesale rather than mutated, because a display change alters the
   *  gradient, the ticks and the accessible name together. */
  colorbar: HTMLElement | null
  onMove: () => void
}

export class ViewportManager {
  private grid: HTMLElement | null = null
  private viewports: Viewport[] = []
  private layout: ViewLayout = '1'
  private primaryIndex = 0
  /** Re-entrancy guard for mirrored camera moves. */
  private syncLock = false
  /** AYNI (multi-globe plan, phase 3): false while someone else drives a globe in a meeting, so each turns on its own. */
  private camerasLinked = true
  private callbacks: ViewportManagerCallbacks = {}

  /**
   * Initialize the manager with a grid element and the starting layout.
   * Creates the initial set of panels and their MapRenderers.
   */
  init(
    grid: HTMLElement,
    initialLayout: ViewLayout = '1',
    callbacks: ViewportManagerCallbacks = {},
  ): void {
    this.grid = grid
    this.callbacks = callbacks
    // Boot into what the device can hold. A deep link can name a layout
    // (`?layout=4`) as directly as the Tools picker can, and `setLayout`'s clamp
    // is downstream of here.
    initialLayout = clampLayoutToPanelBudget(
      initialLayout,
      this.panelBudget(),
      typeof window !== 'undefined' && window.innerHeight >= window.innerWidth,
    )
    this.applyGridTemplate(initialLayout)
    this.layout = initialLayout

    const count = PANEL_COUNT[initialLayout]
    for (let i = 0; i < count; i++) {
      this.addViewport(i)
    }
    this.refreshActiveRenderer()
    this.refreshPrimaryStyling()
  }

  /** Most panels this window may show: the device's decoders and, AYNI, the plan's globes. */
  private panelBudget(): number {
    const plan = this.callbacks.panelLimit?.()
    return Math.min(maxVideoPanels(), typeof plan === 'number' && plan >= 1 ? plan : Infinity)
  }

  /**
   * Change the layout. Adds or removes panels as needed, reusing
   * existing ones. Camera state is copied from the primary to any
   * newly-created panels so the visual transition is seamless.
   *
   * Fires `onLayoutChange` after the panels have been added/removed
   * so callers can resize their parallel per-panel state arrays.
   */
  setLayout(
    layout: ViewLayout,
    /** AYNI: `plan` is the account's plan shrinking the layout; it is not a viewer's choice and emits nothing. */
    trigger: 'tools' | 'orbit' | 'plan' = 'tools',
  ): void {
    if (!this.grid) {
      logger.warn('[ViewportManager] setLayout called before init')
      return
    }
    // What the device can hold, not what was asked for. A link may ask for
    // four panels without knowing what it is running on, and on a phone
    // the third video decoder crashes the tab mid-load — see
    // `clampLayoutToPanelBudget`.
    const requested = layout
    layout = clampLayoutToPanelBudget(
      layout,
      this.panelBudget(),
      typeof window !== 'undefined' && window.innerHeight >= window.innerWidth,
    )
    if (layout !== requested) {
      logger.info(`[ViewportManager] Layout ${requested} reduced to ${layout} for this device or plan`)
    }

    if (layout === this.layout) return

    const targetCount = PANEL_COUNT[layout]
    const previousCount = this.viewports.length

    // Remove excess panels (back-to-front so indices stay stable)
    while (this.viewports.length > targetCount) {
      const vp = this.viewports.pop()!
      this.destroyViewport(vp)
    }

    this.applyGridTemplate(layout)
    this.layout = layout

    // Add new panels seeded from the primary's current camera state
    while (this.viewports.length < targetCount) {
      const idx = this.viewports.length
      this.addViewport(idx)
    }

    // Primary might have been removed — clamp
    const previousPrimary = this.primaryIndex
    if (this.primaryIndex >= this.viewports.length) {
      this.primaryIndex = 0
    }
    this.refreshActiveRenderer()
    this.refreshPrimaryStyling()
    this.resizeAll()

    if (trigger !== 'plan') {
      emit({
        event_type: 'layout_changed',
        layout: layoutForEvent(layout),
        trigger,
      })
    }

    if (previousCount !== targetCount) {
      this.callbacks.onLayoutChange?.(targetCount, previousCount)
    }
    if (previousPrimary !== this.primaryIndex) {
      this.callbacks.onPrimaryChange?.(this.primaryIndex, previousPrimary)
    }
  }

  /** Get the primary (drives playback/screenshots/info panel). */
  getPrimary(): MapRenderer | null {
    return this.viewports[this.primaryIndex]?.renderer ?? null
  }

  /** Get the renderer at a specific slot, or null if out of range. */
  getRendererAt(slot: number): MapRenderer | null {
    return this.viewports[slot]?.renderer ?? null
  }

  /** Get all current renderers in panel order. */
  getAll(): MapRenderer[] {
    return this.viewports.map(v => v.renderer)
  }

  /**
   * Apply a data-encoded viewing transform to every panel.
   *
   * Fanned out rather than applied to the primary alone: in a 2- or
   * 4-globe layout the panels are meant to be compared, and comparing
   * two fields through two different palettes is worse than useless.
   * Panels showing picture datasets ignore it.
   */
  setColorScaleDisplay(display: ColorScaleDisplay): void {
    for (const vp of this.viewports) vp.renderer.setColorScaleDisplay(display)
  }

  /**
   * AYNI: one panel's transform. Colours belong to a dataset now (its
   * display group's standard, or what the viewer saved for it), so each
   * panel draws its own dataset's: two temperature panels still match
   * through their shared standard, and an anomaly panel beside them is
   * not drawn with temperature limits.
   */
  setPanelColorScaleDisplay(index: number, display: ColorScaleDisplay): void {
    this.viewports[index]?.renderer.setColorScaleDisplay(display)
  }

  /** Current layout. */
  getLayout(): ViewLayout {
    return this.layout
  }

  /** Current primary index. */
  getPrimaryIndex(): number {
    return this.primaryIndex
  }

  /** Number of active panels. */
  getPanelCount(): number {
    return this.viewports.length
  }

  /**
   * Mark a panel as primary. Updates the active-renderer singleton so
   * screenshot consumers pick up the change and fires `onPrimaryChange`
   * so callers can rewire bound UI.
   */
  promoteToPrimary(index: number): void {
    if (index < 0 || index >= this.viewports.length) {
      logger.warn(`[ViewportManager] promoteToPrimary: index ${index} out of range`)
      return
    }
    if (index === this.primaryIndex) return
    const previous = this.primaryIndex
    this.primaryIndex = index
    this.refreshActiveRenderer()
    this.refreshPrimaryStyling()
    emit({
      event_type: 'viewport_focus',
      slot_index: String(index),
      layout: layoutForEvent(this.layout),
    })
    this.callbacks.onPrimaryChange?.(this.primaryIndex, previous)
  }

  /** Resize all MapLibre instances — call after CSS grid changes. */
  resizeAll(): void {
    for (const vp of this.viewports) {
      vp.renderer.getMap()?.resize()
    }
  }

  /**
   * Mark a panel as outside the current temporal range (i.e. the
   * primary's real-world date doesn't overlap with this panel's
   * dataset). Adds `.out-of-range` to the panel container so CSS can
   * apply a visual indicator (dim overlay + label). Clears on
   * `isOutOfRange=false` or when the panel's renderer is destroyed.
   */
  setOutOfRange(slot: number, isOutOfRange: boolean): void {
    const vp = this.viewports[slot]
    if (!vp) return
    vp.container.classList.toggle('out-of-range', isOutOfRange)
  }

  /**
   * AYNI (multi-globe plan): what a panel's dataset chip says — the
   * dataset's title, or the "add" label with no dataset. `aria` is the
   * chip's full accessible name (the globe's number and what pressing it
   * does), which the caller phrases.
   */
  setPanelDataset(slot: number, title: string | null, labels: { add: string; aria: string }): void {
    const vp = this.viewports[slot]
    if (!vp) return
    vp.datasetChip.textContent = title ?? labels.add
    vp.datasetChip.classList.toggle('is-empty', !title)
    vp.datasetChip.setAttribute('aria-label', labels.aria)
    vp.datasetChip.title = labels.aria
  }

  /**
   * AYNI (multi-globe plan): the time chip on a panel — the rule its date
   * follows ("Same date", "−30 yr", "Held on 2015-06-01"), or none when
   * the panel is the primary or holds nothing dated. Hidden with null.
   */
  setPanelTimeRule(slot: number, label: string | null, aria?: string): void {
    const vp = this.viewports[slot]
    if (!vp) return
    if (label === null) {
      vp.timeChip.hidden = true
      return
    }
    vp.timeChip.hidden = false
    if (vp.timeChip.textContent !== label) vp.timeChip.textContent = label
    const name = aria ?? label
    if (vp.timeChip.getAttribute('aria-label') !== name) {
      vp.timeChip.setAttribute('aria-label', name)
      vp.timeChip.title = name
    }
  }

  /** AYNI (multi-globe plan): the date a panel shows, beside its chips; hidden with null. Cheap when unchanged. */
  setPanelDate(slot: number, text: string | null): void {
    const vp = this.viewports[slot]
    if (!vp) return
    if (text === null) {
      if (!vp.dateLabel.hidden) vp.dateLabel.hidden = true
      return
    }
    if (vp.dateLabel.hidden) vp.dateLabel.hidden = false
    if (vp.dateLabel.textContent !== text) vp.dateLabel.textContent = text
  }

  /** AYNI: the panel's time chip element, as the anchor of its menu (tests, and the caller's focus return). */
  panelTimeChip(slot: number): HTMLButtonElement | null {
    return this.viewports[slot]?.timeChip ?? null
  }

  /**
   * AYNI (multi-globe plan, phase 3): link or unlink the globes' cameras.
   * Linked (the default), turning one turns them all; unlinked, each turns
   * on its own — while someone drives a globe in a meeting.
   */
  setCamerasLinked(linked: boolean): void {
    if (this.camerasLinked === linked) return
    this.camerasLinked = linked
    // Linked again: the others take the primary's camera at once.
    if (linked) this.syncCameras(this.primaryIndex)
  }

  camerasAreLinked(): boolean {
    return this.camerasLinked
  }

  /** AYNI (multi-globe plan): who drives a globe, on its head row ("Ana drives"); null takes it off. */
  setPanelDriver(slot: number, label: string | null): void {
    const vp = this.viewports[slot]
    if (!vp) return
    if (label === null) {
      vp.driverChip?.remove()
      vp.driverChip = null
      vp.container.classList.remove('is-driven')
      return
    }
    if (!vp.driverChip) {
      const chip = document.createElement('span')
      chip.className = 'viewport-driver'
      vp.head.appendChild(chip)
      vp.driverChip = chip
    }
    if (vp.driverChip.textContent !== label) vp.driverChip.textContent = label
    vp.container.classList.add('is-driven')
  }

  /** The map of one panel, for a caller that locks or moves one globe (a meeting's driver). */
  panelMap(slot: number): ReturnType<MapRenderer['getMap']> {
    return this.viewports[slot]?.renderer.getMap() ?? null
  }

  /**
   * Mount, update, or clear the "not on the labelled date" notice.
   *
   * The multi-globe layout shows one time label for every panel, derived
   * from the primary. When a sibling is verifiably somewhere else, this
   * is how the panel stops the label from speaking for it — it names the
   * date the panel is actually on rather than just marking it wrong.
   *
   * Mutually exclusive with the out-of-range treatment by construction:
   * a panel whose range does not cover the labelled date is reported as
   * `uncovered` and never reaches here, so the two never stack.
   *
   * Takes the already-formatted date the panel is actually showing —
   * the caller owns date formatting, this owns the sentence around it.
   * `null` hides the notice.
   */
  setPanelTimeNotice(slot: number, shownDate: string | null): void {
    const vp = this.viewports[slot]
    if (!vp) return
    vp.noticeDate = shownDate
    this.renderPanelNotice(slot)
  }

  /**
   * Mark a panel whose stream has failed terminally.
   *
   * Distinct from the time notice because the cause is knowable and
   * permanent: the panel is not merely behind the label, it has stopped
   * and will not catch up on its own.
   */
  setPanelStreamNotice(slot: number, failed: boolean): void {
    const vp = this.viewports[slot]
    if (!vp) return
    vp.streamFailed = failed
    this.renderPanelNotice(slot)
  }

  /**
   * Paint whichever notice this panel has earned.
   *
   * A failed stream outranks a time mismatch. Both describe the same
   * observable — this panel is not showing the moment the shared label
   * claims — but the failure says *why*, and unlike a mismatch it will
   * not resolve on the next frame. Showing the weaker one on top of it
   * would replace an explanation with a symptom.
   */
  private renderPanelNotice(slot: number): void {
    const vp = this.viewports[slot]
    if (!vp) return

    const text = vp.streamFailed
      ? t('viewport.panel.streamFailed')
      : vp.noticeDate
        ? t('viewport.panel.timeMismatch', { date: vp.noticeDate })
        : null

    if (!text) {
      if (vp.timeNotice) vp.timeNotice.classList.add('hidden')
      return
    }

    if (!vp.timeNotice) {
      const el = document.createElement('div')
      el.className = 'panel-time-notice'
      // Assertive would interrupt; this is a correction to something
      // already on screen, not an alert.
      el.setAttribute('role', 'status')
      vp.container.appendChild(el)
      vp.timeNotice = el
    }
    if (vp.timeNotice.textContent !== text) vp.timeNotice.textContent = text
    vp.timeNotice.classList.remove('hidden')
  }

  /**
   * Mount, update, or clear the floating legend inside a panel.
   *
   * - `legendLink` non-null → render an <img> button showing the
   *   legend; clicking it invokes `onClick` so callers can open
   *   their full-size modal. The button is lazily created on first
   *   call and reused on subsequent calls.
   * - `legendLink` null → hide the legend element if it exists.
   *
   * The visible/hidden toggle is managed via a `.hidden` class so
   * the DOM + event listener persist across toggles — the caller
   * doesn't have to re-wire the click handler on every change.
   */
  setPanelLegend(
    slot: number,
    legendLink: string | null,
    options: { title?: string; onClick?: () => void } = {},
  ): void {
    const vp = this.viewports[slot]
    if (!vp) return

    if (!legendLink) {
      if (vp.legend) vp.legend.classList.add('hidden')
      return
    }

    if (!vp.legend) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'panel-legend'
      const img = document.createElement('img')
      img.alt = 'Dataset legend'
      btn.appendChild(img)
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation()
        const handler = (vp.legend as HTMLButtonElement & { _onClick?: () => void })._onClick
        handler?.()
      })
      vp.container.appendChild(btn)
      vp.legend = btn
    }

    const img = vp.legend.querySelector('img')
    if (img && img.src !== legendLink) {
      img.src = legendLink
    }
    if (options.title) {
      vp.legend.title = `${options.title} — tap to enlarge`
      vp.legend.setAttribute('aria-label', `${options.title} legend — tap to enlarge`)
    } else {
      vp.legend.title = 'Dataset legend — tap to enlarge'
      vp.legend.setAttribute('aria-label', 'Dataset legend — tap to enlarge')
    }
    // Stash the current click handler on the element so the single
    // stable click listener above can dispatch to the latest one.
    ;(vp.legend as HTMLButtonElement & { _onClick?: () => void })._onClick = options.onClick
    vp.legend.classList.remove('hidden')
  }

  /**
   * Mount (or clear) a rendered colorbar on a panel.
   *
   * A sibling of `setPanelLegend` rather than a mode inside it. The
   * legend path takes a URL and owns an `<img>`; a colorbar is built
   * from the row's `ColorScale` and rebuilt whenever the display
   * transform changes, so folding the two together would mean one
   * method with two disjoint halves. Callers show at most one — see
   * `refreshPanelLegends` in `main.ts`, which prefers the colorbar
   * whenever the dataset carries a scale, because for a data-encoded
   * row the uploaded legend image describes at best the same thing and
   * at worst an older encode.
   */
  /** AYNI: the floating legend a panel shows now, its colour bar or its picture (for `legendPlacement`). */
  panelLegendElement(slot: number): HTMLElement | null {
    const vp = this.viewports[slot]
    if (!vp) return null
    if (vp.colorbar) return vp.colorbar
    return vp.legend && !vp.legend.classList.contains('hidden') ? vp.legend : null
  }

  setPanelColorbar(slot: number, element: HTMLElement | null): void {
    const vp = this.viewports[slot]
    if (!vp) return
    vp.colorbar?.remove()
    vp.colorbar = null
    if (!element) return
    vp.container.appendChild(element)
    vp.colorbar = element
  }

  /**
   * Show or hide a loading overlay on a specific panel. The overlay
   * is lazily created on first use and toggled via a `.hidden` class
   * thereafter. When `isLoading` is true, the panel shows a centered
   * spinner + optional message and the globe content dims behind it.
   * When false, the overlay is hidden and the panel is fully visible.
   */
  setPanelLoading(slot: number, isLoading: boolean, message?: string): void {
    const vp = this.viewports[slot]
    if (!vp) return

    let overlay = vp.container.querySelector('.panel-loading') as HTMLDivElement | null
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.className = 'panel-loading hidden'
      overlay.innerHTML = `
        <div class="panel-loading-spinner"></div>
        <span class="panel-loading-label">Loading\u2026</span>
      `
      vp.container.appendChild(overlay)
    }

    const label = overlay.querySelector('.panel-loading-label')
    if (label) label.textContent = message || 'Loading\u2026'

    overlay.classList.toggle('hidden', !isLoading)
  }

  /** Show/hide every panel's legend element without changing the
   *  underlying dataset binding. Used by the Legend toggle in Tools. */
  setAllLegendsVisible(visible: boolean): void {
    for (const vp of this.viewports) {
      if (!vp.legend) continue
      // Only show when the element has a loaded src — empty panels
      // shouldn't suddenly flash a legend frame.
      const img = vp.legend.querySelector('img') as HTMLImageElement | null
      const hasSrc = !!img?.src
      vp.legend.classList.toggle('hidden', !visible || !hasSrc)
    }
  }

  /** Dispose all viewports. */
  dispose(): void {
    for (const vp of this.viewports) {
      this.destroyViewport(vp)
    }
    this.viewports = []
    setActiveMapRenderer(null)
    this.grid = null
    this.callbacks = {}
  }

  // --- internals ---

  private applyGridTemplate(layout: ViewLayout): void {
    if (!this.grid) return
    this.grid.style.display = 'grid'
    // Explicit template with named areas so we can reliably assign
    // each panel to a grid cell via grid-area: a/b/c/d.
    this.grid.style.gridTemplate = GRID_TEMPLATE[layout]
    this.grid.setAttribute('data-layout', layout)
  }

  private addViewport(index: number): void {
    if (!this.grid) return

    const container = document.createElement('div')
    container.className = 'map-viewport'
    container.style.position = 'relative'
    container.style.width = '100%'
    container.style.height = '100%'
    container.style.background = '#000'
    container.style.overflow = 'hidden'
    container.setAttribute('data-viewport-index', String(index))
    // Assign to a named grid area — a/b/c/d in panel order.
    container.style.gridArea = String.fromCharCode('a'.charCodeAt(0) + index)
    this.grid.appendChild(container)

    const renderer = new MapRenderer()
    const canvasId = index === 0 ? 'globe-canvas' : `globe-canvas-${index}`
    const getLayerId = this.callbacks.getLayerIdForSlot
      ? () => this.callbacks.getLayerIdForSlot!(index)
      : undefined
    renderer.init(container, { canvasId, slotIndex: index, getLayerId })

    // Primary-indicator pill: shown on every panel, numbered 1-based.
    // Click on a non-primary pill promotes that panel to primary. The
    // primary pill is styled as "active" and does nothing on click.
    // Hidden entirely when there's only one panel — no need for a
    // picker when there's nothing to pick.
    // AYNI (multi-globe plan): the pill sits in a row with the dataset
    // chip (what this globe shows, or how to give it something), the time
    // chip (how its date follows globe 1) and the date it is on.
    const head = document.createElement('div')
    head.className = 'viewport-head'
    const indicator = document.createElement('button')
    indicator.type = 'button'
    indicator.className = 'viewport-indicator'
    indicator.textContent = String(index + 1)
    indicator.title = t('viewport.panel.pill.title', { n: index + 1 })
    indicator.setAttribute('aria-label', t('viewport.panel.pill.aria', { n: index + 1 }))
    indicator.addEventListener('click', (ev) => {
      ev.stopPropagation()
      this.promoteToPrimary(index)
    })
    head.appendChild(indicator)
    const datasetChip = document.createElement('button')
    datasetChip.type = 'button'
    datasetChip.className = 'viewport-dataset is-empty'
    datasetChip.textContent = t('viewport.panel.addDataset')
    datasetChip.setAttribute('aria-label', t('viewport.panel.emptyChip.aria', { n: index + 1 }))
    datasetChip.addEventListener('click', (ev) => {
      ev.stopPropagation()
      this.callbacks.onDatasetChip?.(index, datasetChip)
    })
    head.appendChild(datasetChip)
    const timeChip = document.createElement('button')
    timeChip.type = 'button'
    timeChip.className = 'viewport-time'
    timeChip.hidden = true
    timeChip.setAttribute('aria-haspopup', 'menu')
    timeChip.addEventListener('click', (ev) => {
      ev.stopPropagation()
      this.callbacks.onTimeChip?.(index, timeChip)
    })
    head.appendChild(timeChip)
    const dateLabel = document.createElement('span')
    dateLabel.className = 'viewport-date'
    dateLabel.hidden = true
    head.appendChild(dateLabel)
    container.appendChild(head)

    // If there's already a primary, copy its camera state so new panels
    // don't flash the default center before their first sync.
    const primary = this.viewports[this.primaryIndex]?.renderer.getMap()
    if (primary) {
      const map = renderer.getMap()
      if (map) {
        // Run after the new map's 'load' so the camera is definitely set
        map.once('load', () => {
          map.jumpTo({
            center: primary.getCenter(),
            zoom: primary.getZoom(),
            bearing: primary.getBearing(),
            pitch: primary.getPitch(),
          })
        })
      }
    }

    const onMove = () => this.syncCameras(index)
    renderer.getMap()?.on('move', onMove)

    this.viewports.push({ index, container, renderer, head, indicator, datasetChip, timeChip, dateLabel, driverChip: null, legend: null, colorbar: null, timeNotice: null, noticeDate: null, streamFailed: false, onMove })
  }

  private destroyViewport(vp: Viewport): void {
    // Remove the move listener before disposing so dispose() doesn't
    // trigger a cascading sync while the renderer is being torn down.
    vp.renderer.getMap()?.off('move', vp.onMove)
    vp.renderer.dispose()
    vp.container.remove()
  }

  /**
   * Mirror the camera state from the source panel to every sibling.
   * Uses `jumpTo` (instantaneous) so siblings don't lag or animate,
   * and a re-entrancy guard so the mirrored `move` events don't
   * re-enter this function and recurse.
   */
  private syncCameras(sourceIdx: number): void {
    if (this.syncLock || !this.camerasLinked) return
    if (this.viewports.length <= 1) return

    const sourceMap = this.viewports[sourceIdx]?.renderer.getMap()
    if (!sourceMap) return

    const center = sourceMap.getCenter()
    const zoom = sourceMap.getZoom()
    const bearing = sourceMap.getBearing()
    const pitch = sourceMap.getPitch()

    this.syncLock = true
    try {
      for (let i = 0; i < this.viewports.length; i++) {
        if (i === sourceIdx) continue
        const siblingMap = this.viewports[i].renderer.getMap()
        if (!siblingMap) continue
        siblingMap.jumpTo({ center, zoom, bearing, pitch })
      }
    } finally {
      this.syncLock = false
    }
  }

  /** Update the module-level active-renderer slot to point at primary. */
  private refreshActiveRenderer(): void {
    const primary = this.viewports[this.primaryIndex]?.renderer ?? null
    setActiveMapRenderer(primary)
  }

  /**
   * Update `.is-primary` class + indicator state on every panel.
   * Hides the indicator entirely in single-viewport mode — there's
   * nothing to switch to.
   */
  private refreshPrimaryStyling(): void {
    const singleView = this.viewports.length <= 1
    for (const vp of this.viewports) {
      const isPrimary = vp.index === this.primaryIndex
      vp.container.classList.toggle('is-primary', isPrimary)
      vp.indicator.classList.toggle('is-primary', isPrimary)
      vp.indicator.setAttribute('aria-pressed', String(isPrimary))
      vp.indicator.style.display = singleView ? 'none' : ''
      // AYNI: the whole row goes with it — a single globe keeps the chrome it had.
      vp.head.style.display = singleView ? 'none' : ''
    }
  }
}
